import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { amount, money } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/dashboard.css';

interface Dashboard {
  currency: string;
  kpis: Array<{ key: string; label: string; value: number; display: string; tone?: string; detail?: string }>;
  revenue: Array<{ month: string; billedMinor: number; collectedMinor: number }>;
  aging: Array<{ label: string; count: number; amountMinor: number }>;
  todo: {
    drafts: Array<{ id: string; typeLabel: string; number: string; title: string | null; totalMinor: number; currency: string }>;
    unallocatedPayments: Array<{ id: string; number: string; totalMinor: number; currency: string }>;
    overdueInvoices: Array<{ id: string; number: string; dueMinor: number; currency: string; daysOverdue: number }>;
  };
  alerts: Array<{ id: string; severity: string; title: string; body: string; daysToDue: number | null }>;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const monthLabel = (m: string): string => MONTHS[Number(m.split('-')[1]) - 1] ?? m;

const AGING_COLORS = ['#205c3e', '#c99a2e', '#c2743c', '#b1503a', '#7e3327'];

/* Curva suave (Catmull-Rom → Bézier) para el gráfico de ingresos. */
function smooth(points: Array<{ x: number; y: number }>): string {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function RevenueChart({ data }: { data: Dashboard['revenue'] }) {
  const W = 560, H = 200, padL = 8, padR = 8, padT = 16, padB = 26;
  const iw = W - padL - padR, ih = H - padT - padB;
  const max = Math.max(1, ...data.map((d) => Math.max(d.billedMinor, d.collectedMinor)));
  const x = (i: number) => padL + (data.length === 1 ? iw / 2 : (iw * i) / (data.length - 1));
  const y = (v: number) => padT + ih * (1 - v / max);

  const collected = data.map((d, i) => ({ x: x(i), y: y(d.collectedMinor) }));
  const billed = data.map((d, i) => ({ x: x(i), y: y(d.billedMinor) }));
  const line = smooth(collected);
  const area = `${line} L ${collected[collected.length - 1].x} ${padT + ih} L ${collected[0].x} ${padT + ih} Z`;

  return (
    <svg className="rev" viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((g) => (
        <line key={g} className="rev__grid" x1={padL} x2={W - padR} y1={padT + ih * g} y2={padT + ih * g} />
      ))}
      <path className="rev__area" d={area} />
      <path className="rev__billed" d={smooth(billed)} />
      <path className="rev__line" d={line} />
      {collected.map((p, i) => <circle key={i} className="rev__dot" cx={p.x} cy={p.y} r={3.5} />)}
      {data.map((d, i) => (
        <text key={d.month} className="rev__axis" x={x(i)} y={H - 8} textAnchor="middle">{monthLabel(d.month)}</text>
      ))}
    </svg>
  );
}

export function Dashboard() {
  const { company } = useSession();
  const nav = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!company) return;
    setData(null);
    setErr(null);
    api.get<Dashboard>('/dashboard').then(setData).catch((e) => setErr(e instanceof Error ? e.message : 'Error'));
  }, [company]);

  if (err) return <main className="main"><div className="card"><div className="empty">{err}</div></div></main>;
  if (!data) return <main className="main"><div className="faint" style={{ padding: 40 }}>Cargando el panel…</div></main>;

  const agingTotal = data.aging.reduce((s, a) => s + a.amountMinor, 0);
  const todoTotal = data.todo.drafts.length + data.todo.unallocatedPayments.length + data.todo.overdueInvoices.length;
  const today = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <main className="main main--wide">
      <div className="dash-hero">
        <div>
          <span className="eyebrow">Panel</span>
          <div className="dash-hello">{company?.tradeName}</div>
          <div className="dash-sub">Resumen de hoy, {today}.</div>
        </div>
      </div>

      {/* Banda de cifras */}
      <div className="stat-band">
        {data.kpis.map((k) => (
          <div key={k.key} className="stat">
            <div className="stat__label">
              {k.tone && <span className="stat__dot" style={{ background: k.tone === 'good' ? 'var(--accent)' : 'var(--clay)' }} />}
              {k.label}
            </div>
            <div className={`stat__value${k.tone === 'good' ? ' stat__value--good' : ''}${k.tone === 'bad' ? ' stat__value--bad' : ''}`}>{k.display}</div>
          </div>
        ))}
      </div>

      <div className="dash-grid">
        {/* Izquierda */}
        <div className="col gap-32">
          <div className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">Ingresos</span>
              <div className="rev-legend">
                <span><i style={{ background: 'var(--mist)' }} /> Facturado</span>
                <span><i style={{ background: 'var(--accent)' }} /> Cobrado</span>
              </div>
            </div>
            <RevenueChart data={data.revenue} />
          </div>

          <div className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">Debt aging</span>
              <span className="dsec__meta">{money(agingTotal, data.currency)}</span>
            </div>
            {agingTotal === 0 ? (
              <div className="feed__empty"><Icon name="check" size={17} style={{ color: 'var(--accent)' }} />Sin deuda pendiente.</div>
            ) : (
              <>
                <div className="aging-stack">
                  {data.aging.map((a, i) => a.amountMinor > 0 && (
                    <div key={a.label} className="aging-stack__seg" style={{ width: `${(a.amountMinor / agingTotal) * 100}%`, background: AGING_COLORS[i] }} title={`${a.label}: ${amount(a.amountMinor, data.currency)}`} />
                  ))}
                </div>
                <div className="aging-legend">
                  {data.aging.map((a, i) => a.amountMinor > 0 && (
                    <div key={a.label} className="aging-legend__item">
                      <span className="aging-legend__dot" style={{ background: AGING_COLORS[i] }} />
                      <span className="aging-legend__label">{a.label}</span>
                      <span className="aging-legend__amt">{amount(a.amountMinor, data.currency)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Derecha */}
        <div className="col gap-32">
          <div className="dsec">
            <div className="dsec__head">
              <span className="dsec__title">Needs your attention</span>
              {todoTotal > 0 && <span className="dsec__meta">{todoTotal} pendiente{todoTotal === 1 ? '' : 's'}</span>}
            </div>
            <div className="feed">
              {todoTotal === 0 && <div className="feed__empty"><Icon name="check" size={17} style={{ color: 'var(--accent)' }} />All up to date.</div>}

              {data.todo.overdueInvoices.map((d) => (
                <button key={d.id} className="feed__item" onClick={() => nav(`/documentos/${d.id}`)}>
                  <span className="feed__ico" style={{ background: 'var(--clay-tint)' }}><Icon name="alert" size={16} style={{ color: 'var(--clay)' }} /></span>
                  <span className="feed__body">
                    <span className="feed__title">Factura {d.number} vencida</span>
                    <span className="feed__meta">{d.daysOverdue} days ago</span>
                  </span>
                  <span className="feed__amt" style={{ color: 'var(--clay)' }}>{money(d.dueMinor, d.currency)}</span>
                </button>
              ))}

              {data.todo.unallocatedPayments.map((d) => (
                <button key={d.id} className="feed__item" onClick={() => nav(`/documentos/${d.id}`)}>
                  <span className="feed__ico"><Icon name="wallet" size={16} style={{ color: 'var(--accent)' }} /></span>
                  <span className="feed__body">
                    <span className="feed__title">Pago {d.number} sin asignar</span>
                    <span className="feed__meta">Reconcile it with an invoice</span>
                  </span>
                  <span className="feed__amt">{money(d.totalMinor, d.currency)}</span>
                </button>
              ))}

              {data.todo.drafts.map((d) => (
                <button key={d.id} className="feed__item" onClick={() => nav(`/documentos/${d.id}`)}>
                  <span className="feed__ico"><Icon name="doc" size={16} /></span>
                  <span className="feed__body">
                    <span className="feed__title">{d.typeLabel} {d.number} en borrador</span>
                    <span className="feed__meta">{d.title ?? 'Untitled'}</span>
                  </span>
                  <span className="feed__amt">{money(d.totalMinor, d.currency)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="dsec">
            <div className="dsec__head"><span className="dsec__title">Alertas</span></div>
            <div className="feed">
              {data.alerts.length === 0 && <div className="feed__empty"><Icon name="check" size={17} style={{ color: 'var(--accent)' }} />Sin alertas abiertas.</div>}
              {data.alerts.map((a) => (
                <div key={a.id} className="feed__item" style={{ cursor: 'default' }}>
                  <span className="feed__ico" style={{ background: a.severity === 'critical' ? 'var(--clay-tint)' : 'var(--card-sunken)' }}>
                    <Icon name={a.severity === 'critical' ? 'alert' : 'clock'} size={16} style={{ color: a.severity === 'critical' ? 'var(--clay)' : 'var(--amber)' }} />
                  </span>
                  <span className="feed__body">
                    <span className="feed__title">{a.title}</span>
                    <span className="feed__meta">{a.body}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
